// src/store/upcomingSession/upcomingSessionSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchUpcomingSessions,
  fetchSessionById,
  createSession,
  updateSession,
  deleteSession
} from './upcomingSessionThunk';

const initialState = {
  sessions: [],
  currentSession: null,
  
  // Loading states
  loading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  
  // Error states
  error: null,
  createError: null,
  updateError: null,
  deleteError: null,
  
  // Success messages
  successMessage: null,
  
  // Pagination
  total: 0
};

const upcomingSessionSlice = createSlice({
  name: 'upcomingSession',
  initialState,
  reducers: {
    setCurrentSession: (state, action) => {
      state.currentSession = action.payload;
    },
    
    clearCurrentSession: (state) => {
      state.currentSession = null;
    },
    
    clearError: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    
    // Filter sessions by status
    filterSessionsByStatus: (state, action) => {
      const status = action.payload;
      if (status === 'all') {
        return;
      }
      state.sessions = state.sessions.filter(s => s.status === status);
    }
  },
  
  extraReducers: (builder) => {
    // FETCH ALL SESSIONS
    builder
      .addCase(fetchUpcomingSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUpcomingSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload?.sessions || [];
        state.total = action.payload?.total || 0;
      })
      .addCase(fetchUpcomingSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch sessions';
        state.sessions = [];
        state.total = 0;
      })
    
    // FETCH SESSION BY ID
      .addCase(fetchSessionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSessionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload?.session || null;
      })
      .addCase(fetchSessionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch session';
        state.currentSession = null;
      })
    
    // CREATE SESSION
      .addCase(createSession.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.successMessage = null;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.createLoading = false;
        state.successMessage = action.payload.message || 'Session created successfully';
        state.currentSession = action.payload.session;
        
        // Add to sessions list
        if (action.payload.session) {
          state.sessions.unshift(action.payload.session);
          state.total += 1;
        }
      })
      .addCase(createSession.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload || 'Failed to create session';
      })
    
    // UPDATE SESSION
      .addCase(updateSession.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.successMessage = null;
      })
      .addCase(updateSession.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.successMessage = action.payload?.message || 'Session updated';
        state.currentSession = action.payload?.session || null;
        
        // Update in list
        if (action.payload?.session) {
          const index = state.sessions.findIndex(
            s => s.id === action.payload.session.id
          );
          if (index !== -1) {
            state.sessions[index] = {
              ...state.sessions[index],
              ...action.payload.session
            };
          }
        }
      })
      .addCase(updateSession.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || 'Failed to update session';
      })
    
    // DELETE SESSION
      .addCase(deleteSession.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.successMessage = null;
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.successMessage = action.payload?.message || 'Session deleted';
        
        // Remove from list
        if (action.payload?.session_id) {
          state.sessions = state.sessions.filter(
            s => s.id !== action.payload.session_id
          );
          state.total -= 1;
          
          // Clear current if deleted
          if (state.currentSession && state.currentSession.id === action.payload.session_id) {
            state.currentSession = null;
          }
        }
      })
      .addCase(deleteSession.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || 'Failed to delete session';
      });
  }
});

// Actions
export const {
  setCurrentSession,
  clearCurrentSession,
  clearError,
  clearSuccessMessage,
  filterSessionsByStatus
} = upcomingSessionSlice.actions;

// Selectors
export const selectSessions = (state) => state.upcomingSession.sessions;
export const selectCurrentSession = (state) => state.upcomingSession.currentSession;
export const selectSessionLoading = (state) => state.upcomingSession.loading;
export const selectCreateLoading = (state) => state.upcomingSession.createLoading;
export const selectUpdateLoading = (state) => state.upcomingSession.updateLoading;
export const selectDeleteLoading = (state) => state.upcomingSession.deleteLoading;
export const selectSessionError = (state) => state.upcomingSession.error;
export const selectCreateError = (state) => state.upcomingSession.createError;
export const selectUpdateError = (state) => state.upcomingSession.updateError;
export const selectDeleteError = (state) => state.upcomingSession.deleteError;
export const selectSuccessMessage = (state) => state.upcomingSession.successMessage;
export const selectTotal = (state) => state.upcomingSession.total;

export default upcomingSessionSlice.reducer;